import apiService from './api'

/**
 * API сервис для работы с апгрейдером
 */
const upgraderApi = {
  /**
   * Получение списка доступных предметов для апгрейда (шаг 1)
   * Endpoint: GET /api/upgrader/
   * Возвращает список скинов из инвентаря пользователя, доступных для апгрейда
   * @returns {Promise<Array>} Массив предметов с маппингом cost → price
   */
  getAvailableItems: async () => {
    try {
      const response = await apiService.makeRequest('/api/upgrader/', {
        method: 'GET',
      })
      
      // API возвращает { "upgrader": [...] }, извлекаем массив
      const items = response?.upgrader || response || []
      
      // Маппинг данных: cost → price для совместимости с существующим кодом
      if (Array.isArray(items)) {
        const mappedItems = items.map(item => ({
          ...item,
          price: parseInt(item.cost) || 0, // Добавляем поле price из cost
        }))
        
        // Находим самый свежий скин (с минимальным ago_sec)
        const freshestItem = mappedItems.reduce((min, item) => {
          const itemAgoSec = parseInt(item.ago_sec) || Infinity
          const minAgoSec = parseInt(min?.ago_sec) || Infinity
          return itemAgoSec < minAgoSec ? item : min
        }, null)
        
        // Сортируем остальные по цене (от дорогих к дешевым)
        const sortedItems = mappedItems
          .filter(item => item.id !== freshestItem?.id)
          .sort((a, b) => b.price - a.price)
        
        // Если есть самый свежий скин, ставим его первым
        return freshestItem ? [freshestItem, ...sortedItems] : sortedItems
      }
      
      return []
    } catch (error) {
      console.error('Get available items error:', error)
      throw error
    }
  },

  /**
   * Получение мультипликаторов и доступных скинов для выбранного предмета
   * Endpoint: GET /api/upgrader/{itemId}/
   * @param {string} itemId - ID выбранного предмета пользователя
   * @returns {Promise<Object>} Объект с мультипликаторами и информацией о донор-предмете
   */
  getMultipliers: async (itemId) => {
    try {
      console.log(`🔄 Загрузка мультипликаторов для предмета ${itemId}...`)
      const response = await apiService.makeRequest(`/api/upgrader/${itemId}/`, {
        method: 'GET',
      })
      
      console.log('✅ Мультипликаторы загружены:', response)
      
      // API возвращает { "multy": {...}, "donor": {...} }
      return {
        multipliers: response?.multy || {},
        donor: response?.donor || null,
      }
    } catch (error) {
      console.error('❌ Ошибка загрузки мультипликаторов:', error)
      throw error
    }
  },

  /**
   * Расчет шанса успешного апгрейда (шаг 3)
   * Endpoint: GET /api/upgrader/{donorId}/{targetId}/
   * @param {string} donorId - ID исходного предмета (донор)
   * @param {string} targetId - ID целевого предмета
   * @returns {Promise<Object>} Объект с donor, target, chance
   */
  calculateChance: async (donorId, targetId) => {
    try {
      console.log(`🔄 Расчет шанса апгрейда: ${donorId} → ${targetId}`)
      const response = await apiService.makeRequest(`/api/upgrader/${donorId}/${targetId}/`, {
        method: 'GET',
      })

      if (response?.error) {
        throw new Error(response.error)
      }

      if (typeof response?.chance !== 'number') {
        throw new Error('Некорректный ответ сервера')
      }
      
      console.log('✅ Шанс апгрейда получен:', response)
      
      // API возвращает { "donor": {...}, "target": {...}, "chance": 71, "me": {...} }
      return {
        donor: response?.donor || null,
        target: response?.target || null,
        chance: response?.chance || 0,
      }
    } catch (error) {
      console.error('❌ Ошибка расчета шанса:', error)
      throw error
    }
  },

  /**
   * Выполнение апгрейда (шаг 4)
   * Endpoint: GET /api/upgrader/{donorId}/{targetId}/upgrade/
   * @param {string} donorId - ID исходного предмета (донор)
   * @param {string} targetId - ID целевого предмета
   * @returns {Promise<Object>} Результат апгрейда с donor, target, chance, win
   */
  performUpgrade: async (donorId, targetId) => {
    try {
      console.log(`🎰 Выполнение апгрейда: ${donorId} → ${targetId}`)
      const response = await apiService.makeRequest(`/api/upgrader/${donorId}/${targetId}/upgrade/`, {
        method: 'GET',
      })

      if (response?.error) {
        throw new Error(response.error)
      }

      if (typeof response?.win !== 'boolean') {
        throw new Error('Некорректный ответ сервера')
      }
      
      console.log('✅ Результат апгрейда получен:', response)
      
      // API возвращает { "donor": {...}, "target": {...}, "chance": 72, "win": true/false, "firstSuccessUpgrade": true/false, "me": {...} }
      return {
        donor: response?.donor || null,
        target: response?.target || null,
        chance: response?.chance || 0,
        win: response.win,
        firstSuccessUpgrade: response?.firstSuccessUpgrade || false,
      }
    } catch (error) {
      console.error('❌ Ошибка выполнения апгрейда:', error)
      throw error
    }
  },

}

export default upgraderApi
